import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';

export interface ContextFile {
    name: string;
    url?: string;
    type: string;
    size: string;
    path: string;
}

export interface Context {
    id: string;
    name: string;
    description: string;
    context: string;
    content?: string; // Landing page or core ad content
    assignedPersonas: string[]; // List of IDs
    files?: ContextFile[];
    createdAt?: string;
}

type ContextRowWithPersonas = Database['public']['Tables']['contexts']['Row'] & {
    context_personas: { persona_id: string }[] | { persona_id: string } | null;
};

export const contextService = {
    async fetchAll(offset: number = 0, limit: number = 50): Promise<Context[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase.from('contexts').select(`
            *,
            context_personas (persona_id)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

        if (error) throw error;
        if (!data) return [];

        return (data as unknown as ContextRowWithPersonas[]).map((c) => {
            const contextPersonas = Array.isArray(c.context_personas)
                ? c.context_personas
                : c.context_personas
                ? [c.context_personas]
                : [];
            return {
                id: c.id,
                name: c.name || '',
                description: c.description || '',
                context: c.context || '',
                content: c.content || undefined,
                assignedPersonas: contextPersonas.map((cp) => cp.persona_id),
                files: (c.files as unknown as ContextFile[] | undefined) || [],
                createdAt: c.created_at
            };
        });
    },

    async create(context: Omit<Context, 'id'>): Promise<Context> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const insertData: Database['public']['Tables']['contexts']['Insert'] = {
            name: context.name,
            description: context.description,
            context: context.context,
            content: context.content || null,
            user_id: user.id,
            files: (context.files || []) as unknown as Database['public']['Tables']['contexts']['Insert']['files']
        };

        const { data, error } = await supabase.from('contexts').insert(insertData).select().single();

        if (error) throw error;

        if (context.assignedPersonas.length > 0) {
            const relations = context.assignedPersonas.map(pid => ({
                context_id: data.id,
                persona_id: pid
            }));
            await supabase.from('context_personas').insert(relations);
        }

        return { ...context, id: data.id };
    },

    async update(context: Context): Promise<Context> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const updateData: Database['public']['Tables']['contexts']['Update'] = {
            name: context.name,
            description: context.description,
            context: context.context,
            content: context.content || null,
            files: (context.files || []) as unknown as Database['public']['Tables']['contexts']['Update']['files']
        };

        const { error } = await supabase.from('contexts').update(updateData).eq('id', context.id).eq('user_id', user.id);

        if (error) throw error;

        // Update personas relation (delete all and re-insert for simplicity)
        // Note: In production, consider a smarter diff
        await supabase.from('context_personas').delete().eq('context_id', context.id);

        if (context.assignedPersonas.length > 0) {
            const relations = context.assignedPersonas.map(pid => ({
                context_id: context.id,
                persona_id: pid
            }));
            await supabase.from('context_personas').insert(relations);
        }

        return context;
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // 1. Buscar metadados dos arquivos anexados para limpeza de Storage
        try {
            const { data: contextData } = await supabase
                .from('contexts')
                .select('files')
                .eq('id', id)
                .eq('user_id', user.id)
                .single();

            const files = (contextData?.files as Array<{ path?: string }>) || [];
            const filePaths = files.map(f => f.path).filter((p): p is string => Boolean(p));

            if (filePaths.length > 0) {
                await supabase.storage.from('context_files').remove(filePaths);
            }
        } catch (storageErr) {
            console.error('[contextService] Erro ao limpar arquivos do Storage (não bloqueante):', storageErr);
        }

        // 2. Exclusão no banco de dados (aciona CASCADE em conversas, mensagens e insights)
        const { error } = await supabase.from('contexts').delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    }
};
